import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Button, Table, Badge, Form, Modal, Row, Col, Spinner, Alert } from 'react-bootstrap';
import {
    PlusCircle, Edit3, Trash2, Clock, AlertCircle, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RULES_I18N = {
    es: {
        headerTitle: '⚡ Mis Reglas Operativas y Horarios',
        headerSubtitle: 'Configura tus propios bloqueos de agenda (días libres), recargos por horario punta/nocturno, tarifas de última hora o descuentos en horario valle. Se aplican exclusivamente a tu flota.',
        newRuleBtn: 'Nueva Regla',
        refreshBtn: 'Actualizar',
        templatesTitle: 'Crear rápido con una plantilla (1 clic):',
        tplBlockTitle: '🛑 Pausar Operación / Día Libre',
        tplBlockDesc: 'Bloquea tu disponibilidad en un feriado, vacaciones o rango horario donde no operes.',
        tplPeakTitle: '📈 Recargo Punta / Nocturno',
        tplPeakDesc: 'Sube tu tarifa un % en horarios punta, madrugadas o fines de semana.',
        tplUrgencyTitle: '⚡ Urgencia / Última Hora',
        tplUrgencyDesc: 'Cobra un % extra o bloquea reservas que entren con poca anticipación (ej: < 3 hrs).',
        tplValleyTitle: '🏷️ Descuento Horario Valle',
        tplValleyDesc: 'Ofrece un % de descuento en horas de baja demanda para ganar más viajes.',
        activeRulesTitle: 'Mis Reglas Configuradas',
        emptyRulesTitle: 'Aún no tienes reglas personalizadas',
        emptyRulesDesc: 'Haz clic en cualquiera de las 4 plantillas de arriba para crear tu primera regla en segundos.',
        colStatus: 'Estado',
        colRule: 'Regla / Tipo',
        colSchedule: 'Cuándo aplica (Horario / Fechas)',
        colScope: 'Alcance (Zona / Vehículo)',
        colEffect: 'Efecto en tu Tarifa',
        colActions: 'Acciones',
        badgeActive: 'Activa',
        badgeInactive: 'Pausada',
        badgeBlock: '🛑 Bloqueo (No operar)',
        badgeSurcharge: '📈 Recargo',
        badgeUrgency: '⚡ Urgencia',
        badgeDiscount: '🏷️ Descuento',
        allZones: 'Todas mis zonas',
        allVehicles: 'Todos mis vehículos',
        allDays: 'Todos los días',
        allDayHours: 'Todo el día (24h)',
        leadTimePrefix: 'Reservas con menos de',
        leadTimeRangeLabel: '⚡ Reservas entre {min} y {max} hrs antes (bajo {min}h se bloquea)',
        hoursUnit: 'hrs',
        minsUnit: 'min',
        modalCreateTitle: 'Configurar Regla Operativa',
        modalEditTitle: 'Editar Regla Operativa',
        fieldRuleType: '1. ¿Qué tipo de regla quieres aplicar?',
        typeBlock: '🛑 Bloquear disponibilidad (Pausar ventas)',
        typeSurcharge: '📈 Subir tarifa (% Recargo)',
        typeUrgency: '⚡ Regla por Anticipación / Urgencia',
        typeDiscount: '🏷️ Bajar tarifa (% Descuento)',
        fieldName: 'Nombre de la regla (para identificarla)',
        fieldPctSurcharge: 'Porcentaje de recargo (+1% a +100%)',
        fieldPctDiscount: 'Porcentaje de descuento (1% a 40%)',
        fieldUrgencyAction: '¿Qué hacer si reservan con poca anticipación?',
        urgencyActionSurcharge: 'Cobrar recargo porcentual (%) en ventana de urgencia',
        urgencyActionBlock: 'Bloquear reserva (No recibir viajes tan encima)',
        fieldMinLeadTimeHours: 'Anticipación Mínima / Piso (Horas antes del viaje)',
        fieldMinLeadTimeHelp: 'Ej: Si pones 1 hora, NO entrarán viajes para menos de 1 hora (60 min).',
        fieldLeadTimeHours: 'Hasta qué anticipación cobrar recargo (Horas antes)',
        fieldLeadTimeHelp: 'Ej: Si pones 3 horas, el recargo aplicará entre 1 hora y 3 horas antes del viaje.',
        fieldBlockLeadTimeHours: 'Bloquear si faltan menos de (Horas antes del viaje)',
        fieldBlockLeadTimeHelp: 'Ej: Si pones 3 horas, no se aceptarán reservas solicitadas con menos de 3 horas.',
        fieldScheduleMode: '2. ¿Cuándo debe activarse esta regla?',
        modeRecurring: '🔄 Horario Recurrente (Días de semana y Horas)',
        modeDates: '📅 Fechas Específicas (Feriado, Vacaciones, Evento)',
        fieldDaysOfWeek: 'Días de la semana',
        presetAllDays: 'Todos',
        presetWeekdays: 'Lun a Vie',
        presetWeekend: 'Fin de Semana',
        fieldTimeRange: 'Rango de horas (dejar vacío para todo el día 24h)',
        fieldStartTime: 'Hora Inicio',
        fieldEndTime: 'Hora Fin',
        fieldStartDate: 'Desde (Fecha y Hora)',
        fieldEndDate: 'Hasta (Fecha y Hora)',
        fieldScopeTitle: '3. ¿Dónde y en qué vehículos aplica? (Opcional)',
        fieldZone: 'Zona específica',
        fieldVehicles: 'Tipos de vehículo',
        previewTitle: 'Vista previa de impacto:',
        previewBlockText: 'Durante este horario/fecha, tu flota NO aparecerá disponible para nuevas reservas en los filtros seleccionados.',
        previewPriceText: 'Si tu tarifa base es {base}, con esta regla cobrarás {final} ({sign}{pct}%).',
        btnCancel: 'Cancelar',
        btnSave: 'Guardar Regla',
        btnSaving: 'Guardando...',
        confirmDelete: '¿Estás seguro de eliminar esta regla?',
        daysShort: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
        defaultNameBlock: 'Pausa Operativa / Día Libre',
        defaultNamePeak: 'Recargo Horario Punta',
        defaultNameUrgency: 'Recargo Última Hora (1h a 3h)',
        defaultNameDiscount: 'Promo Horario Valle'
    },
    pt: {
        headerTitle: '⚡ Minhas Regras Operacionais e Horários',
        headerSubtitle: 'Configure seus próprios bloqueios de agenda (folgas), adicionais de horário de pico/noturno, tarifas de última hora ou descontos em horário de baixa demanda. Aplicam-se exclusivamente à sua frota.',
        newRuleBtn: 'Nova Regra',
        refreshBtn: 'Atualizar',
        templatesTitle: 'Criar rápido com um modelo (1 clique):',
        tplBlockTitle: '🛑 Pausar Operação / Folga',
        tplBlockDesc: 'Bloqueie sua disponibilidade em feriados, férias ou faixas de horário em que não opere.',
        tplPeakTitle: '📈 Adicional Pico / Noturno',
        tplPeakDesc: 'Aumente sua tarifa em % nos horários de pico, madrugadas ou fins de semana.',
        tplUrgencyTitle: '⚡ Urgência / Última Hora',
        tplUrgencyDesc: 'Cobre % extra ou bloqueie reservas feitas com pouca antecedência (ex: 1h a 3h).',
        tplValleyTitle: '🏷️ Desconto Baixa Demanda',
        tplValleyDesc: 'Ofereça % de desconto em horários tranquilos para ganhar mais viagens.',
        activeRulesTitle: 'Minhas Regras Configuradas',
        emptyRulesTitle: 'Você ainda não tem regras personalizadas',
        emptyRulesDesc: 'Clique em qualquer um dos 4 modelos acima para criar sua primeira regra em segundos.',
        colStatus: 'Status',
        colRule: 'Regra / Tipo',
        colSchedule: 'Quando aplica (Horário / Datas)',
        colScope: 'Escopo (Zona / Veículo)',
        colEffect: 'Efeito na Tarifa',
        colActions: 'Ações',
        badgeActive: 'Ativa',
        badgeInactive: 'Pausada',
        badgeBlock: '🛑 Bloqueio (Não operar)',
        badgeSurcharge: '📈 Adicional',
        badgeUrgency: '⚡ Urgência',
        badgeDiscount: '🏷️ Desconto',
        allZones: 'Todas as minhas zonas',
        allVehicles: 'Todos os meus veículos',
        allDays: 'Todos os dias',
        allDayHours: 'Dia todo (24h)',
        leadTimePrefix: 'Reservas com menos de',
        leadTimeRangeLabel: '⚡ Reservas entre {min} e {max} h antes (abaixo de {min}h bloqueia)',
        hoursUnit: 'h',
        minsUnit: 'min',
        modalCreateTitle: 'Configurar Regra Operacional',
        modalEditTitle: 'Editar Regra Operacional',
        fieldRuleType: '1. Que tipo de regra deseja aplicar?',
        typeBlock: '🛑 Bloquear disponibilidade (Pausar vendas)',
        typeSurcharge: '📈 Aumentar tarifa (% Adicional)',
        typeUrgency: '⚡ Regra por Antecedência / Urgência',
        typeDiscount: '🏷️ Reduzir tarifa (% Desconto)',
        fieldName: 'Nome da regra (para identificação)',
        fieldPctSurcharge: 'Percentual de adicional (+1% a +100%)',
        fieldPctDiscount: 'Percentual de desconto (1% a 40%)',
        fieldUrgencyAction: 'O que fazer se reservarem com pouca antecedência?',
        urgencyActionSurcharge: 'Cobrar adicional percentual (%) na janela de urgência',
        urgencyActionBlock: 'Bloquear reserva (Não aceitar viagens tão em cima da hora)',
        fieldMinLeadTimeHours: 'Antecedência Mínima / Piso (Horas antes da viagem)',
        fieldMinLeadTimeHelp: 'Ex: Se colocar 1 hora, NÃO serão aceitas viagens para menos de 1 hora (60 min).',
        fieldLeadTimeHours: 'Até qual antecedência cobrar adicional (Horas antes)',
        fieldLeadTimeHelp: 'Ex: Se colocar 3 horas, o adicional aplicará entre 1 hora e 3 horas antes da viagem.',
        fieldBlockLeadTimeHours: 'Bloquear se faltar menos de (Horas antes da viagem)',
        fieldBlockLeadTimeHelp: 'Ex: Se colocar 3 horas, não serão aceitas reservas solicitadas com menos de 3 horas.',
        fieldScheduleMode: '2. Quando esta regra deve ativar?',
        modeRecurring: '🔄 Horário Recorrente (Dias da semana e Horas)',
        modeDates: '📅 Datas Específicas (Feriado, Férias, Evento)',
        fieldDaysOfWeek: 'Dias da semana',
        presetAllDays: 'Todos',
        presetWeekdays: 'Seg a Sex',
        presetWeekend: 'Fim de Semana',
        fieldTimeRange: 'Faixa de horário (deixe vazio para 24h)',
        fieldStartTime: 'Hora Início',
        fieldEndTime: 'Hora Fim',
        fieldStartDate: 'De (Data e Hora)',
        fieldEndDate: 'Até (Data e Hora)',
        fieldScopeTitle: '3. Onde e em quais veículos aplica? (Opcional)',
        fieldZone: 'Zona específica',
        fieldVehicles: 'Tipos de veículo',
        previewTitle: 'Prévia de impacto:',
        previewBlockText: 'Durante este horário/data, sua frota NÃO aparecerá disponível para novas reservas nos filtros selecionados.',
        previewPriceText: 'Se sua tarifa base é {base}, com esta regra você receberá {final} ({sign}{pct}%).',
        btnCancel: 'Cancelar',
        btnSave: 'Salvar Regra',
        btnSaving: 'Salvando...',
        confirmDelete: 'Tem certeza que deseja excluir esta regra?',
        daysShort: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
        defaultNameBlock: 'Pausa Operacional / Folga',
        defaultNamePeak: 'Adicional Horário de Pico',
        defaultNameUrgency: 'Adicional Última Hora (1h a 3h)',
        defaultNameDiscount: 'Promo Horário Tranquilo'
    },
    en: {
        headerTitle: '⚡ My Operating Rules & Schedules',
        headerSubtitle: 'Configure your own schedule blocks (days off), peak/night surcharges, last-minute rates, or off-peak discounts. Applied exclusively to your fleet.',
        newRuleBtn: 'New Rule',
        refreshBtn: 'Refresh',
        templatesTitle: 'Quick start with a template (1 click):',
        tplBlockTitle: '🛑 Pause Operations / Day Off',
        tplBlockDesc: 'Block availability on a holiday, vacation, or time window when you do not operate.',
        tplPeakTitle: '📈 Peak / Night Surcharge',
        tplPeakDesc: 'Increase your rate by % during rush hours, late nights, or weekends.',
        tplUrgencyTitle: '⚡ Last-Minute / Min Notice',
        tplUrgencyDesc: 'Charge extra % or block bookings made with short notice (e.g. 1h to 3h).',
        tplValleyTitle: '🏷️ Off-Peak Discount',
        tplValleyDesc: 'Offer a % discount during quiet hours to win more trips.',
        activeRulesTitle: 'My Configured Rules',
        emptyRulesTitle: 'No custom rules configured yet',
        emptyRulesDesc: 'Click any of the 4 templates above to create your first rule in seconds.',
        colStatus: 'Status',
        colRule: 'Rule / Type',
        colSchedule: 'When it applies (Schedule / Dates)',
        colScope: 'Scope (Zone / Vehicle)',
        colEffect: 'Effect on Your Rate',
        colActions: 'Actions',
        badgeActive: 'Active',
        badgeInactive: 'Paused',
        badgeBlock: '🛑 Blocked (Stop-Sell)',
        badgeSurcharge: '📈 Surcharge',
        badgeUrgency: '⚡ Last-Minute',
        badgeDiscount: '🏷️ Discount',
        allZones: 'All my zones',
        allVehicles: 'All my vehicles',
        allDays: 'Every day',
        allDayHours: 'All day (24h)',
        leadTimePrefix: 'Bookings within',
        leadTimeRangeLabel: '⚡ Bookings between {min} and {max} hrs before (blocked under {min}h)',
        hoursUnit: 'hrs',
        minsUnit: 'min',
        modalCreateTitle: 'Configure Operating Rule',
        modalEditTitle: 'Edit Operating Rule',
        fieldRuleType: '1. What type of rule do you want to apply?',
        typeBlock: '🛑 Block availability (Stop-Sell)',
        typeSurcharge: '📈 Increase rate (% Surcharge)',
        typeUrgency: '⚡ Short-Notice / Urgency Rule',
        typeDiscount: '🏷️ Lower rate (% Discount)',
        fieldName: 'Rule name (for your reference)',
        fieldPctSurcharge: 'Surcharge percentage (+1% to +100%)',
        fieldPctDiscount: 'Discount percentage (1% to 40%)',
        fieldUrgencyAction: 'What should happen on short-notice bookings?',
        urgencyActionSurcharge: 'Charge percentage surcharge (%) in urgency window',
        urgencyActionBlock: 'Block booking (Do not accept last-minute trips)',
        fieldMinLeadTimeHours: 'Minimum Notice / Floor (Hours before pickup)',
        fieldMinLeadTimeHelp: 'E.g. Setting 1 hour blocks any trip requested less than 1 hour (60 min) before pickup.',
        fieldLeadTimeHours: 'Apply surcharge up to (Hours before pickup)',
        fieldLeadTimeHelp: 'E.g. Setting 3 hours applies the surcharge between 1 hour and 3 hours before pickup.',
        fieldBlockLeadTimeHours: 'Block if requested less than (Hours before pickup)',
        fieldBlockLeadTimeHelp: 'E.g. Setting 3 hours blocks any booking made less than 3 hours before pickup.',
        fieldScheduleMode: '2. When should this rule trigger?',
        modeRecurring: '🔄 Recurring Schedule (Days of Week & Hours)',
        modeDates: '📅 Specific Dates (Holiday, Vacation, Event)',
        fieldDaysOfWeek: 'Days of the week',
        presetAllDays: 'All',
        presetWeekdays: 'Mon–Fri',
        presetWeekend: 'Weekend',
        fieldTimeRange: 'Time window (leave blank for 24h)',
        fieldStartTime: 'Start Time',
        fieldEndTime: 'End Time',
        fieldStartDate: 'From (Date & Time)',
        fieldEndDate: 'Until (Date & Time)',
        fieldScopeTitle: '3. Where and which vehicles? (Optional)',
        fieldZone: 'Specific zone',
        fieldVehicles: 'Vehicle types',
        previewTitle: 'Impact preview:',
        previewBlockText: 'During this schedule/date window, your fleet will NOT appear available for new bookings matching these filters.',
        previewPriceText: 'If your base rate is {base}, with this rule you will earn {final} ({sign}{pct}%).',
        btnCancel: 'Cancel',
        btnSave: 'Save Rule',
        btnSaving: 'Saving...',
        confirmDelete: 'Are you sure you want to delete this rule?',
        daysShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        defaultNameBlock: 'Operations Pause / Day Off',
        defaultNamePeak: 'Peak Hour Surcharge',
        defaultNameUrgency: 'Last-Minute Surcharge (1h to 3h)',
        defaultNameDiscount: 'Off-Peak Promo'
    }
};

const ProviderRulesManager = ({ providerId, tariffs = [], currencyCode = 'CLP', formatCurrency }) => {
    const { language } = useLanguage();
    const tr = RULES_I18N[language] || RULES_I18N.es;

    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    // Form state
    const [ruleCategory, setRuleCategory] = useState('surcharge'); // 'block' | 'surcharge' | 'urgency' | 'discount'
    const [name, setName] = useState('');
    const [pctValue, setPctValue] = useState(20);
    const [urgencyAction, setUrgencyAction] = useState('surcharge'); // 'surcharge' | 'block'
    const [minLeadTimeHours, setMinLeadTimeHours] = useState(1);
    const [leadTimeHours, setLeadTimeHours] = useState(3);
    const [scheduleMode, setScheduleMode] = useState('recurring'); // 'recurring' | 'dates'
    const [selectedDays, setSelectedDays] = useState(['1', '2', '3', '4', '5', '6', '7']);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [startDatetime, setStartDatetime] = useState('');
    const [endDatetime, setEndDatetime] = useState('');
    const [specificZoneId, setSpecificZoneId] = useState('');
    const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

    // Derive unique zones and vehicle types from the provider's tariffs
    const providerZones = useMemo(() => {
        const map = new Map();
        (tariffs || []).forEach(t => {
            if (t.origin_zone && t.origin_zone.id) {
                map.set(t.origin_zone.id, t.origin_zone.name);
            }
            if (t.destination_zone && t.destination_zone.id) {
                map.set(t.destination_zone.id, t.destination_zone.name);
            }
        });
        return Array.from(map.entries())
            .map(([id, zName]) => ({ id, name: zName }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [tariffs]);

    const providerVehicleTypes = useMemo(() => {
        const map = new Map();
        (tariffs || []).forEach(t => {
            if (t.vehicle_type && t.vehicle_type.id) {
                map.set(t.vehicle_type.id, t.vehicle_type.name);
            }
        });
        return Array.from(map.entries())
            .map(([id, vName]) => ({ id: String(id), name: vName }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [tariffs]);

    const sampleBaseRate = useMemo(() => {
        const valid = (tariffs || []).find(t => Number(t.cost_local) > 0);
        if (valid) return Number(valid.cost_local);
        return currencyCode === 'CLP' ? 25000 : (currencyCode === 'BRL' ? 150 : 40);
    }, [tariffs, currencyCode]);

    const fmtMoney = useCallback((val) => {
        if (typeof formatCurrency === 'function') return formatCurrency(val);
        return `${currencyCode} ${Number(val || 0).toLocaleString()}`;
    }, [formatCurrency, currencyCode]);

    const fetchRules = useCallback(async () => {
        if (!providerId) return;
        setLoading(true);
        setErrorMsg('');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/transport/price-rules/`, {
                params: { provider_id: providerId },
                headers: { Authorization: `Bearer ${token}` }
            });
            setRules(res.data || []);
        } catch (err) {
            console.error('Error fetching provider rules:', err);
            setErrorMsg(err.response?.data?.detail || 'Error al cargar reglas operativas.');
        } finally {
            setLoading(false);
        }
    }, [providerId]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const openTemplate = (tplType) => {
        setEditingRule(null);
        setErrorMsg('');
        setSpecificZoneId('');
        setSelectedVehicleIds([]);

        if (tplType === 'block') {
            setRuleCategory('block');
            setName(tr.defaultNameBlock);
            setScheduleMode('dates');
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const yyyy = tomorrow.getFullYear();
            const mm = String(tomorrow.getMonth() + 1).padStart(2, '0');
            const dd = String(tomorrow.getDate()).padStart(2, '0');
            setStartDatetime(`${yyyy}-${mm}-${dd}T00:00`);
            setEndDatetime(`${yyyy}-${mm}-${dd}T23:59`);
            setSelectedDays(['1', '2', '3', '4', '5', '6', '7']);
            setStartTime('');
            setEndTime('');
        } else if (tplType === 'surcharge') {
            setRuleCategory('surcharge');
            setName(tr.defaultNamePeak);
            setPctValue(20);
            setScheduleMode('recurring');
            setSelectedDays(['1', '2', '3', '4', '5', '6', '7']);
            setStartTime('22:00');
            setEndTime('06:00');
            setStartDatetime('');
            setEndDatetime('');
        } else if (tplType === 'urgency') {
            setRuleCategory('urgency');
            setName(tr.defaultNameUrgency);
            setUrgencyAction('surcharge');
            setPctValue(25);
            setMinLeadTimeHours(1);
            setLeadTimeHours(3);
            setScheduleMode('recurring');
            setSelectedDays(['1', '2', '3', '4', '5', '6', '7']);
            setStartTime('');
            setEndTime('');
            setStartDatetime('');
            setEndDatetime('');
        } else if (tplType === 'discount') {
            setRuleCategory('discount');
            setName(tr.defaultNameDiscount);
            setPctValue(15);
            setScheduleMode('recurring');
            setSelectedDays(['1', '2', '3', '4']);
            setStartTime('10:00');
            setEndTime('16:00');
            setStartDatetime('');
            setEndDatetime('');
        }
        setShowModal(true);
    };

    const openEditModal = (rule) => {
        setEditingRule(rule);
        setErrorMsg('');
        setName(rule.name || '');

        const hasLeadTime = rule.max_lead_time_minutes !== null && rule.max_lead_time_minutes !== undefined;
        if (hasLeadTime) {
            setRuleCategory('urgency');
            setUrgencyAction(rule.is_blocking ? 'block' : 'surcharge');
            setPctValue(Math.abs(Number(rule.value || 20)));
            setMinLeadTimeHours(
                rule.min_lead_time_minutes !== null && rule.min_lead_time_minutes !== undefined
                    ? Math.max(1, Math.round(Number(rule.min_lead_time_minutes) / 60))
                    : 1
            );
            setLeadTimeHours(Math.max(1, Math.round(Number(rule.max_lead_time_minutes) / 60)));
        } else if (rule.is_blocking) {
            setRuleCategory('block');
            setPctValue(0);
        } else if (Number(rule.value) < 0) {
            setRuleCategory('discount');
            setPctValue(Math.abs(Number(rule.value || 10)));
        } else {
            setRuleCategory('surcharge');
            setPctValue(Math.abs(Number(rule.value || 20)));
        }

        if (rule.start_datetime && rule.end_datetime) {
            setScheduleMode('dates');
            setStartDatetime(String(rule.start_datetime).slice(0, 16));
            setEndDatetime(String(rule.end_datetime).slice(0, 16));
            setSelectedDays(['1', '2', '3', '4', '5', '6', '7']);
            setStartTime('');
            setEndTime('');
        } else {
            setScheduleMode('recurring');
            setStartDatetime('');
            setEndDatetime('');
            if (rule.days_of_week) {
                const parsed = [];
                rule.days_of_week.split(',').forEach(part => {
                    const p = part.trim();
                    if (p.includes('-')) {
                        const [s, e] = p.split('-').map(Number);
                        for (let i = s; i <= e; i++) parsed.push(String(i));
                    } else if (p) {
                        parsed.push(p);
                    }
                });
                setSelectedDays(parsed.length > 0 ? parsed : ['1', '2', '3', '4', '5', '6', '7']);
            } else {
                setSelectedDays(['1', '2', '3', '4', '5', '6', '7']);
            }
            setStartTime(rule.start_time || '');
            setEndTime(rule.end_time || '');
        }

        setSpecificZoneId(rule.specific_zone_id ? String(rule.specific_zone_id) : '');
        setSelectedVehicleIds(
            rule.vehicle_type_ids
                ? rule.vehicle_type_ids.split(',').map(v => v.trim()).filter(Boolean)
                : []
        );
        setShowModal(true);
    };

    const toggleDay = (dayStr) => {
        setSelectedDays(prev =>
            prev.includes(dayStr) ? prev.filter(d => d !== dayStr) : [...prev, dayStr].sort()
        );
    };

    const toggleVehicleId = (vId) => {
        setSelectedVehicleIds(prev =>
            prev.includes(vId) ? prev.filter(v => v !== vId) : [...prev, vId]
        );
    };

    const handleSaveRule = async (e) => {
        if (e) e.preventDefault();
        if (!name.trim()) return;

        setSaving(true);
        setErrorMsg('');

        const isBlocking =
            ruleCategory === 'block' ||
            (ruleCategory === 'urgency' && urgencyAction === 'block');

        let signedPct = 0;
        if (!isBlocking) {
            const cleanPct = Math.max(1, Math.min(ruleCategory === 'discount' ? 40 : 100, Number(pctValue) || 0));
            signedPct = ruleCategory === 'discount' ? -cleanPct : cleanPct;
        }

        const isAllDays = selectedDays.length === 7 || selectedDays.length === 0;
        const cleanMinHrs = Math.max(1, Number(minLeadTimeHours) || 1);
        const cleanMaxHrs = Math.max(cleanMinHrs, Number(leadTimeHours) || 3);

        const payload = {
            name: name.trim(),
            is_active: editingRule ? editingRule.is_active : true,
            is_blocking: isBlocking,
            is_exclusive: false,
            priority: 1,
            value_type: 'percentage',
            value: signedPct,
            cost_value: signedPct,
            provider_ids: String(providerId),
            max_lead_time_minutes:
                ruleCategory === 'urgency'
                    ? Math.round((urgencyAction === 'block' ? Math.max(1, Number(leadTimeHours) || 3) : cleanMaxHrs) * 60)
                    : null,
            min_lead_time_minutes:
                ruleCategory === 'urgency' && urgencyAction === 'surcharge'
                    ? Math.round(cleanMinHrs * 60)
                    : null,
            days_of_week: scheduleMode === 'recurring' && !isAllDays ? selectedDays.join(',') : null,
            start_time: scheduleMode === 'recurring' && startTime ? startTime : null,
            end_time: scheduleMode === 'recurring' && endTime ? endTime : null,
            start_datetime: scheduleMode === 'dates' && startDatetime ? startDatetime : null,
            end_datetime: scheduleMode === 'dates' && endDatetime ? endDatetime : null,
            specific_zone_id: specificZoneId ? Number(specificZoneId) : null,
            vehicle_type_ids: selectedVehicleIds.length > 0 ? selectedVehicleIds.join(',') : null
        };

        try {
            const token = localStorage.getItem('token');
            if (editingRule) {
                await axios.put(`${API_URL}/transport/price-rules/${editingRule.id}`, payload, {
                    params: { provider_id: providerId },
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${API_URL}/transport/price-rules/`, payload, {
                    params: { provider_id: providerId },
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            setShowModal(false);
            await fetchRules();
        } catch (err) {
            console.error('Error saving provider rule:', err);
            setErrorMsg(err.response?.data?.detail || 'Error al guardar la regla.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/transport/price-rules/${rule.id}`,
                { is_active: !rule.is_active },
                {
                    params: { provider_id: providerId },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, is_active: !r.is_active } : r)));
        } catch (err) {
            console.error('Error toggling rule:', err);
        }
    };

    const handleDeleteRule = async (rule) => {
        if (!window.confirm(tr.confirmDelete)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/transport/price-rules/${rule.id}`, {
                params: { provider_id: providerId },
                headers: { Authorization: `Bearer ${token}` }
            });
            setRules(prev => prev.filter(r => r.id !== rule.id));
        } catch (err) {
            console.error('Error deleting rule:', err);
        }
    };

    const formatRuleSchedule = (rule) => {
        const parts = [];
        if (rule.max_lead_time_minutes) {
            const maxHrs = +(Number(rule.max_lead_time_minutes) / 60).toFixed(1);
            if (rule.min_lead_time_minutes && !rule.is_blocking) {
                const minHrs = +(Number(rule.min_lead_time_minutes) / 60).toFixed(1);
                parts.push(
                    tr.leadTimeRangeLabel
                        .replace(/\{min\}/g, String(minHrs))
                        .replace(/\{max\}/g, String(maxHrs))
                );
            } else {
                parts.push(`⚡ ${tr.leadTimePrefix} ${maxHrs} ${tr.hoursUnit}`);
            }
        }
        if (rule.start_datetime && rule.end_datetime) {
            const s = String(rule.start_datetime).replace('T', ' ').slice(0, 16);
            const e = String(rule.end_datetime).replace('T', ' ').slice(0, 16);
            parts.push(`📅 ${s} → ${e}`);
        } else {
            if (rule.days_of_week) {
                const dayNames = rule.days_of_week
                    .split(',')
                    .map(d => tr.daysShort[Number(d.trim()) - 1] || d)
                    .join(', ');
                parts.push(`🗓️ ${dayNames}`);
            } else {
                parts.push(`🗓️ ${tr.allDays}`);
            }
            if (rule.start_time && rule.end_time) {
                parts.push(`⏰ ${rule.start_time} - ${rule.end_time}`);
            } else {
                parts.push(`⏰ ${tr.allDayHours}`);
            }
        }
        return parts.join(' · ');
    };

    const formatRuleScope = (rule) => {
        const zoneObj = providerZones.find(z => Number(z.id) === Number(rule.specific_zone_id));
        const zoneLabel = zoneObj ? `📍 ${zoneObj.name}` : (rule.specific_zone_id ? `📍 Zona #${rule.specific_zone_id}` : `🌍 ${tr.allZones}`);

        let vehLabel = `🚘 ${tr.allVehicles}`;
        if (rule.vehicle_type_ids) {
            const ids = rule.vehicle_type_ids.split(',').map(v => v.trim()).filter(Boolean);
            const names = ids.map(id => {
                const found = providerVehicleTypes.find(v => String(v.id) === String(id));
                return found ? found.name : `#${id}`;
            });
            if (names.length > 0) vehLabel = `🚘 ${names.join(', ')}`;
        }
        return { zoneLabel, vehLabel };
    };

    const renderRuleBadge = (rule) => {
        if (rule.is_blocking) {
            return <Badge bg="danger" className="px-2 py-1">{tr.badgeBlock}</Badge>;
        }
        if (rule.max_lead_time_minutes) {
            return <Badge bg="warning" text="dark" className="px-2 py-1">{tr.badgeUrgency} (+{rule.value}%)</Badge>;
        }
        if (Number(rule.value) < 0) {
            return <Badge bg="success" className="px-2 py-1">{tr.badgeDiscount} ({rule.value}%)</Badge>;
        }
        return <Badge bg="primary" className="px-2 py-1">{tr.badgeSurcharge} (+{rule.value}%)</Badge>;
    };

    // Preview calculation inside Modal
    const isModalBlocking = ruleCategory === 'block' || (ruleCategory === 'urgency' && urgencyAction === 'block');
    const modalCleanPct = Math.max(1, Math.min(ruleCategory === 'discount' ? 40 : 100, Number(pctValue) || 0));
    const modalSignedPct = ruleCategory === 'discount' ? -modalCleanPct : modalCleanPct;
    const modalFinalRate = Math.round(sampleBaseRate * (1 + modalSignedPct / 100));

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                <div>
                    <h4 className="fw-bold text-dark mb-1">{tr.headerTitle}</h4>
                    <p className="text-muted small mb-0">{tr.headerSubtitle}</p>
                </div>
                <div className="d-flex align-items-center gap-2 w-100 w-md-auto">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center justify-content-center gap-1"
                        onClick={fetchRules}
                        disabled={loading}
                    >
                        <RefreshCw size={15} className={loading ? 'spin' : ''} /> {tr.refreshBtn}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        className="fw-bold d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-md-grow-0 shadow-sm"
                        onClick={() => openTemplate('surcharge')}
                    >
                        <PlusCircle size={16} /> {tr.newRuleBtn}
                    </Button>
                </div>
            </div>

            {errorMsg && (
                <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible className="py-2 small">
                    <AlertCircle size={16} className="me-1" /> {errorMsg}
                </Alert>
            )}

            {/* 4 Guided 1-Click Templates */}
            <div className="mb-4">
                <div className="small fw-bold text-uppercase text-muted mb-2">{tr.templatesTitle}</div>
                <Row className="g-2 g-md-3">
                    <Col xs={12} sm={6} lg={3}>
                        <Card
                            className="h-100 border-0 shadow-sm cursor-pointer"
                            style={{ borderLeft: '4px solid #dc3545', cursor: 'pointer', transition: 'transform 0.15s' }}
                            onClick={() => openTemplate('block')}
                        >
                            <Card.Body className="p-3">
                                <div className="fw-bold text-danger mb-1">{tr.tplBlockTitle}</div>
                                <div className="text-muted small" style={{ fontSize: '0.78rem' }}>{tr.tplBlockDesc}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <Card
                            className="h-100 border-0 shadow-sm cursor-pointer"
                            style={{ borderLeft: '4px solid #0d6efd', cursor: 'pointer', transition: 'transform 0.15s' }}
                            onClick={() => openTemplate('surcharge')}
                        >
                            <Card.Body className="p-3">
                                <div className="fw-bold text-primary mb-1">{tr.tplPeakTitle}</div>
                                <div className="text-muted small" style={{ fontSize: '0.78rem' }}>{tr.tplPeakDesc}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <Card
                            className="h-100 border-0 shadow-sm cursor-pointer"
                            style={{ borderLeft: '4px solid #ffc107', cursor: 'pointer', transition: 'transform 0.15s' }}
                            onClick={() => openTemplate('urgency')}
                        >
                            <Card.Body className="p-3">
                                <div className="fw-bold text-dark mb-1">{tr.tplUrgencyTitle}</div>
                                <div className="text-muted small" style={{ fontSize: '0.78rem' }}>{tr.tplUrgencyDesc}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <Card
                            className="h-100 border-0 shadow-sm cursor-pointer"
                            style={{ borderLeft: '4px solid #198754', cursor: 'pointer', transition: 'transform 0.15s' }}
                            onClick={() => openTemplate('discount')}
                        >
                            <Card.Body className="p-3">
                                <div className="fw-bold text-success mb-1">{tr.tplValleyTitle}</div>
                                <div className="text-muted small" style={{ fontSize: '0.78rem' }}>{tr.tplValleyDesc}</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Rules List */}
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                    <span className="fw-bold text-dark">{tr.activeRulesTitle} ({rules.length})</span>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                        </div>
                    ) : rules.length === 0 ? (
                        <div className="text-center py-5 px-3">
                            <Clock size={36} className="text-muted mb-2 opacity-50" />
                            <h6 className="fw-bold text-dark mb-1">{tr.emptyRulesTitle}</h6>
                            <p className="text-muted small mb-0">{tr.emptyRulesDesc}</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="d-none d-md-block">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light text-muted small text-uppercase">
                                        <tr>
                                            <th className="ps-3">{tr.colStatus}</th>
                                            <th>{tr.colRule}</th>
                                            <th>{tr.colSchedule}</th>
                                            <th>{tr.colScope}</th>
                                            <th>{tr.colEffect}</th>
                                            <th className="text-end pe-3">{tr.colActions}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rules.map(rule => {
                                            const { zoneLabel, vehLabel } = formatRuleScope(rule);
                                            return (
                                                <tr key={rule.id} className={!rule.is_active ? 'opacity-50 bg-light' : ''}>
                                                    <td className="ps-3">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`switch-rule-${rule.id}`}
                                                            checked={Boolean(rule.is_active)}
                                                            onChange={() => handleToggleActive(rule)}
                                                            label={
                                                                <span className={`small fw-semibold ${rule.is_active ? 'text-success' : 'text-muted'}`}>
                                                                    {rule.is_active ? tr.badgeActive : tr.badgeInactive}
                                                                </span>
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="fw-bold text-dark">{rule.name}</div>
                                                    </td>
                                                    <td className="small text-secondary">
                                                        {formatRuleSchedule(rule)}
                                                    </td>
                                                    <td className="small">
                                                        <div>{zoneLabel}</div>
                                                        <div className="text-muted">{vehLabel}</div>
                                                    </td>
                                                    <td>{renderRuleBadge(rule)}</td>
                                                    <td className="text-end pe-3">
                                                        <div className="d-inline-flex gap-1">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => openEditModal(rule)}
                                                            >
                                                                <Edit3 size={14} />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteRule(rule)}
                                                            >
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="d-md-none p-2 space-y-2">
                                {rules.map(rule => {
                                    const { zoneLabel, vehLabel } = formatRuleScope(rule);
                                    return (
                                        <Card key={rule.id} className={`border shadow-sm mb-2 ${!rule.is_active ? 'opacity-50 bg-light' : ''}`}>
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <div className="fw-bold text-dark mb-1">{rule.name}</div>
                                                        {renderRuleBadge(rule)}
                                                    </div>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`mob-switch-rule-${rule.id}`}
                                                        checked={Boolean(rule.is_active)}
                                                        onChange={() => handleToggleActive(rule)}
                                                    />
                                                </div>
                                                <div className="small text-secondary mb-2">
                                                    {formatRuleSchedule(rule)}
                                                </div>
                                                <div className="d-flex flex-wrap gap-2 small text-muted mb-3">
                                                    <span>{zoneLabel}</span>
                                                    <span>·</span>
                                                    <span>{vehLabel}</span>
                                                </div>
                                                <div className="d-flex justify-content-end gap-2 border-top pt-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="d-flex align-items-center gap-1"
                                                        onClick={() => openEditModal(rule)}
                                                    >
                                                        <Edit3 size={14} /> Editar
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-flex align-items-center gap-1"
                                                        onClick={() => handleDeleteRule(rule)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Create / Edit */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold fs-5">
                        {editingRule ? tr.modalEditTitle : tr.modalCreateTitle}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveRule}>
                    <Modal.Body className="p-3 p-md-4">
                        {/* 1. Rule Category */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-uppercase text-muted">{tr.fieldRuleType}</Form.Label>
                            <Row className="g-2">
                                {[
                                    { id: 'block', label: tr.typeBlock, color: 'danger' },
                                    { id: 'surcharge', label: tr.typeSurcharge, color: 'primary' },
                                    { id: 'urgency', label: tr.typeUrgency, color: 'warning' },
                                    { id: 'discount', label: tr.typeDiscount, color: 'success' }
                                ].map(opt => (
                                    <Col xs={12} sm={6} key={opt.id}>
                                        <Button
                                            type="button"
                                            variant={ruleCategory === opt.id ? opt.color : `outline-${opt.color}`}
                                            className="w-100 text-start fw-semibold py-2 px-3 small"
                                            onClick={() => setRuleCategory(opt.id)}
                                        >
                                            {opt.label}
                                        </Button>
                                    </Col>
                                ))}
                            </Row>
                        </Form.Group>

                        <Row className="g-3 mb-3">
                            <Col xs={12} md={isModalBlocking && ruleCategory !== 'urgency' ? 12 : 6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small">{tr.fieldName}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>

                            {ruleCategory === 'surcharge' && (
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small">{tr.fieldPctSurcharge}</Form.Label>
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                max={100}
                                                value={pctValue}
                                                onChange={e => setPctValue(e.target.value)}
                                                required
                                            />
                                            <span className="fw-bold text-primary fs-5">%</span>
                                        </div>
                                    </Form.Group>
                                </Col>
                            )}

                            {ruleCategory === 'discount' && (
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small">{tr.fieldPctDiscount}</Form.Label>
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                max={40}
                                                value={pctValue}
                                                onChange={e => setPctValue(e.target.value)}
                                                required
                                            />
                                            <span className="fw-bold text-success fs-5">%</span>
                                        </div>
                                    </Form.Group>
                                </Col>
                            )}

                            {ruleCategory === 'urgency' && (
                                <>
                                    <Col xs={12} md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold small">{tr.fieldUrgencyAction}</Form.Label>
                                            <Form.Select
                                                value={urgencyAction}
                                                onChange={e => setUrgencyAction(e.target.value)}
                                            >
                                                <option value="surcharge">{tr.urgencyActionSurcharge}</option>
                                                <option value="block">{tr.urgencyActionBlock}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    {urgencyAction === 'surcharge' ? (
                                        <>
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold small text-danger">{tr.fieldMinLeadTimeHours}</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min={1}
                                                        max={72}
                                                        value={minLeadTimeHours}
                                                        onChange={e => setMinLeadTimeHours(e.target.value)}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted" style={{ fontSize: '0.73rem' }}>
                                                        {tr.fieldMinLeadTimeHelp}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold small">{tr.fieldLeadTimeHours}</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min={Math.max(1, Number(minLeadTimeHours) || 1)}
                                                        max={72}
                                                        value={leadTimeHours}
                                                        onChange={e => setLeadTimeHours(e.target.value)}
                                                        required
                                                    />
                                                    <Form.Text className="text-muted" style={{ fontSize: '0.73rem' }}>
                                                        {tr.fieldLeadTimeHelp}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold small">{tr.fieldPctSurcharge}</Form.Label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Form.Control
                                                            type="number"
                                                            min={1}
                                                            max={100}
                                                            value={pctValue}
                                                            onChange={e => setPctValue(e.target.value)}
                                                            required
                                                        />
                                                        <span className="fw-bold text-warning fs-5">%</span>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    ) : (
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small text-danger">{tr.fieldBlockLeadTimeHours}</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min={1}
                                                    max={72}
                                                    value={leadTimeHours}
                                                    onChange={e => setLeadTimeHours(e.target.value)}
                                                    required
                                                />
                                                <Form.Text className="text-muted" style={{ fontSize: '0.73rem' }}>
                                                    {tr.fieldBlockLeadTimeHelp}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    )}
                                </>
                            )}
                        </Row>

                        <hr className="my-3" />

                        {/* 2. Schedule Mode */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold small text-uppercase text-muted">{tr.fieldScheduleMode}</Form.Label>
                            <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={scheduleMode === 'recurring' ? 'dark' : 'outline-secondary'}
                                    className="flex-grow-1 py-2 fw-semibold"
                                    onClick={() => setScheduleMode('recurring')}
                                >
                                    {tr.modeRecurring}
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={scheduleMode === 'dates' ? 'dark' : 'outline-secondary'}
                                    className="flex-grow-1 py-2 fw-semibold"
                                    onClick={() => setScheduleMode('dates')}
                                >
                                    {tr.modeDates}
                                </Button>
                            </div>

                            {scheduleMode === 'recurring' ? (
                                <div className="bg-light p-3 rounded border">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                        <span className="small fw-semibold">{tr.fieldDaysOfWeek}</span>
                                        <div className="d-flex gap-1">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="py-0 px-2"
                                                style={{ fontSize: '0.72rem' }}
                                                onClick={() => setSelectedDays(['1', '2', '3', '4', '5', '6', '7'])}
                                            >
                                                {tr.presetAllDays}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="py-0 px-2"
                                                style={{ fontSize: '0.72rem' }}
                                                onClick={() => setSelectedDays(['1', '2', '3', '4', '5'])}
                                            >
                                                {tr.presetWeekdays}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="py-0 px-2"
                                                style={{ fontSize: '0.72rem' }}
                                                onClick={() => setSelectedDays(['6', '7'])}
                                            >
                                                {tr.presetWeekend}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {tr.daysShort.map((dLabel, idx) => {
                                            const dNum = String(idx + 1);
                                            const active = selectedDays.includes(dNum);
                                            return (
                                                <Button
                                                    key={dNum}
                                                    type="button"
                                                    size="sm"
                                                    variant={active ? 'primary' : 'outline-secondary'}
                                                    className="fw-bold flex-grow-1"
                                                    onClick={() => toggleDay(dNum)}
                                                >
                                                    {dLabel}
                                                </Button>
                                            );
                                        })}
                                    </div>

                                    <div className="small fw-semibold mb-1">{tr.fieldTimeRange}</div>
                                    <Row className="g-2">
                                        <Col xs={6}>
                                            <Form.Label className="small text-muted mb-1">{tr.fieldStartTime}</Form.Label>
                                            <Form.Control
                                                type="time"
                                                size="sm"
                                                value={startTime}
                                                onChange={e => setStartTime(e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Label className="small text-muted mb-1">{tr.fieldEndTime}</Form.Label>
                                            <Form.Control
                                                type="time"
                                                size="sm"
                                                value={endTime}
                                                onChange={e => setEndTime(e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            ) : (
                                <div className="bg-light p-3 rounded border">
                                    <Row className="g-2">
                                        <Col xs={12} sm={6}>
                                            <Form.Label className="small fw-semibold mb-1">{tr.fieldStartDate}</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={startDatetime}
                                                onChange={e => setStartDatetime(e.target.value)}
                                                required={scheduleMode === 'dates'}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Form.Label className="small fw-semibold mb-1">{tr.fieldEndDate}</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={endDatetime}
                                                onChange={e => setEndDatetime(e.target.value)}
                                                required={scheduleMode === 'dates'}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </Form.Group>

                        <hr className="my-3" />

                        {/* 3. Scope (Zone & Vehicles) */}
                        <div className="mb-3">
                            <div className="fw-bold small text-uppercase text-muted mb-2">{tr.fieldScopeTitle}</div>
                            <Row className="g-3">
                                <Col xs={12} md={6}>
                                    <Form.Label className="small fw-semibold">{tr.fieldZone}</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={specificZoneId}
                                        onChange={e => setSpecificZoneId(e.target.value)}
                                    >
                                        <option value="">🌍 {tr.allZones}</option>
                                        {providerZones.map(z => (
                                            <option key={z.id} value={z.id}>{z.name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Label className="small fw-semibold">{tr.fieldVehicles}</Form.Label>
                                    <div className="d-flex flex-wrap gap-1">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={selectedVehicleIds.length === 0 ? 'dark' : 'outline-secondary'}
                                            onClick={() => setSelectedVehicleIds([])}
                                        >
                                            {tr.allVehicles}
                                        </Button>
                                        {providerVehicleTypes.map(vt => {
                                            const active = selectedVehicleIds.includes(String(vt.id));
                                            return (
                                                <Button
                                                    key={vt.id}
                                                    type="button"
                                                    size="sm"
                                                    variant={active ? 'primary' : 'outline-secondary'}
                                                    onClick={() => toggleVehicleId(String(vt.id))}
                                                >
                                                    {vt.name}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Live Impact Preview */}
                        <Alert variant={isModalBlocking ? 'danger' : (ruleCategory === 'discount' ? 'success' : 'info')} className="mb-0 py-2 px-3 small">
                            <div className="fw-bold mb-1">{tr.previewTitle}</div>
                            {isModalBlocking ? (
                                <div>{tr.previewBlockText}</div>
                            ) : (
                                <div>
                                    {tr.previewPriceText
                                        .replace('{base}', fmtMoney(sampleBaseRate))
                                        .replace('{final}', fmtMoney(modalFinalRate))
                                        .replace('{sign}', modalSignedPct >= 0 ? '+' : '')
                                        .replace('{pct}', String(modalSignedPct))}
                                </div>
                            )}
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
                            {tr.btnCancel}
                        </Button>
                        <Button variant="primary" size="sm" type="submit" className="fw-bold px-3" disabled={saving}>
                            {saving ? tr.btnSaving : tr.btnSave}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ProviderRulesManager;
